'use client';
import { createContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback, useContext } from 'react';
import type { Session } from '@supabase/supabase-js';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { toast } from '@/components/ui/use-toast';
import { AuthUser, AuthContextType } from '@/types/auth';
import { convertUserToAuthUser } from '@/utils/authUtils';
import { patientHomeAfterSignIn } from '@/lib/pendingPrescription';

/**
 * The Supabase client is loaded after the page has rendered, not with it
 * (2026-10-16). This provider wraps every page, so a static import put
 * supabase-js (about 60 KB of compressed script, over a quarter of a public
 * page's first load) on the critical path of pages that only need it to find
 * out whether someone is signed in. Pages and components that read data still
 * import the client directly and load it with themselves.
 */
type SupabaseClient = (typeof import('@/integrations/supabase/client'))['supabase'];
let supabaseClient: Promise<SupabaseClient> | null = null;
const getSupabase = () =>
  (supabaseClient ??= import('@/integrations/supabase/client').then((m) => m.supabase));

// Shown only when a sign-in needs a second factor, so it loads then.
const MFAVerifyChallenge = dynamic(
  () => import('@/components/auth/MFAVerifyChallenge').then((m) => m.MFAVerifyChallenge),
  { ssr: false },
);

export type { AuthUser, AuthContextType } from '@/types/auth';

// Create and export AuthContext
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export useAuth hook directly from here to avoid circular dependencies
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context as AuthContextType;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Whose role has been read from the database. The session alone carries no
  // role, and the dashboards route on it: /dashboard sends a user with no
  // role to the upload page, and the pharmacy and admin dashboards send them
  // away. Until 2026-10-16 isLoading turned false before the role was read,
  // so every signed-in user opening /dashboard (where sign-in and the
  // pharmacy confirmation email lead) landed on the upload page, and the
  // admin dashboard sent its user to /login.
  const [roleReadFor, setRoleReadFor] = useState<string | null>(null);
  const roleReadForRef = useRef<string | null>(null);
  const currentUserId = useRef<string | null>(null);
  const profileRequests = useRef(new Map<string, Promise<void>>());
  // MFA challenge state - set when signIn detects enrolled TOTP factors
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const pendingNavigateTo = useRef('/dashboard');
  const router = useRouter();

  const fetchProfile = useCallback((userId: string) => {
    const inFlight = profileRequests.current.get(userId);
    if (inFlight) return inFlight;
    const request = (async () => {
      try {
        const supabase = await getSupabase();
        // Fetch profile and role in parallel
        const readAll = () => Promise.all([
          supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
          supabase.from('user_roles').select('role').eq('user_id', userId).maybeSingle(),
          supabase.rpc('get_own_pharmacy', { _user_id: userId })
        ]);
        let [profileResult, roleResult, pharmacyResult] = await readAll();
        if (roleResult.error && currentUserId.current === userId) {
          // One retry: the dashboards route on this answer.
          await new Promise((resolve) => setTimeout(resolve, 800));
          [profileResult, roleResult, pharmacyResult] = await readAll();
        }
        // Signed out, or someone else signed in, while this was loading.
        if (currentUserId.current !== userId) return;

        if (profileResult.error) console.error('Error fetching profile:', profileResult.error);
        if (roleResult.error) console.error('Error fetching role:', roleResult.error);

        const profileData = profileResult.data;
        const pharmacyData = pharmacyResult.data as Record<string, any> | null;
        const roleFound = roleResult.error ? undefined : (roleResult.data?.role as AuthUser['role']);

        if (profileData || roleFound) {
          setProfile({ ...(profileData ?? {}), role: roleFound, pharmacy: pharmacyData });
          setUser(prev => prev && prev.id === userId ? {
            ...prev,
            // A failed read keeps the role already known.
            role: roleResult.error ? prev.role : roleFound,
            firstName: profileData?.first_name ?? prev.firstName,
            lastName: profileData?.last_name ?? prev.lastName,
            country: profileData?.country || pharmacyData?.country,
            pharmacyName: pharmacyData?.pharmacy_name,
            pharmacyType: undefined,
            isVerified: pharmacyData?.is_verified
          } : prev);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        profileRequests.current.delete(userId);
        if (currentUserId.current === userId) {
          roleReadForRef.current = userId;
          setRoleReadFor(userId);
        }
      }
    })();
    profileRequests.current.set(userId, request);
    return request;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    // supabase-js reports the same session again and again: on every return
    // to the tab (SIGNED_IN), on each token refresh, and once from both the
    // listener and getSession at start. Replacing the user each time dropped
    // the role until the profile was read again, and in that gap the
    // pharmacy dashboard sent a pharmacist who switched tabs back to
    // /dashboard and on to the upload page. Keep what was read for the same
    // user, and refresh it quietly.
    const applySession = (session: Session | null) => {
      setSession(session);
      const sessionUser = session?.user;
      if (!sessionUser) {
        currentUserId.current = null;
        roleReadForRef.current = null;
        setRoleReadFor(null);
        setUser(null);
        setProfile(null);
        return;
      }
      currentUserId.current = sessionUser.id;
      setUser(prev => {
        if (!prev || prev.id !== sessionUser.id) return convertUserToAuthUser(sessionUser);
        const same = prev.email === sessionUser.email
          && JSON.stringify(prev.user_metadata) === JSON.stringify(sessionUser.user_metadata)
          && JSON.stringify(prev.app_metadata) === JSON.stringify(sessionUser.app_metadata);
        return same ? prev : {
          ...prev,
          email: sessionUser.email,
          user_metadata: sessionUser.user_metadata,
          app_metadata: sessionUser.app_metadata,
        };
      });
      if (roleReadForRef.current !== sessionUser.id) {
        // Pages that route on the role wait for this, so read it now.
        void fetchProfile(sessionUser.id);
      } else if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => fetchProfile(sessionUser.id), { timeout: 2000 });
      } else {
        setTimeout(() => fetchProfile(sessionUser.id), 0);
      }
    };

    getSupabase().then((supabase) => {
      if (cancelled) return;
      // Set up auth state listener FIRST
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        applySession(session);
      });
      unsubscribe = () => subscription.unsubscribe();

      // THEN check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        applySession(session);
        setIsLoading(false);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);

      const { data, error } = await (await getSupabase()).auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: 'Login failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error, data: null };
      }

      // Additional check to ensure user data is properly received
      if (!data || !data.user) {
        const customError = new Error('User data not received after successful login');
        toast({
          title: 'Login issue',
          description: 'Successfully authenticated but user data is missing. Please try again.',
          variant: 'destructive',
        });
        return { error: customError, data: null };
      }

      // Check for enrolled TOTP MFA factors - if present, gate navigation
      const { data: factorsData } = await (await getSupabase()).auth.mfa.listFactors();
      const totpFactor = factorsData?.totp?.[0];
      if (totpFactor) {
        pendingNavigateTo.current = patientHomeAfterSignIn();
        setMfaFactorId(totpFactor.id);
        setMfaPending(true);
        // Caller receives mfaRequired signal; MFAVerifyChallenge is rendered by provider
        return { error: null, data, mfaRequired: true };
      }

      toast({
        title: 'Login successful',
        description: 'Welcome back to Script Unlock!',
      });

      // A request typed before signing in waits on the upload page (2026-10-15).
      router.push(patientHomeAfterSignIn());
      return { error: null, data };
    } catch (error: any) {
      toast({
        title: 'Login failed',
        description: error.message,
        variant: 'destructive',
      });
      return { error, data: null };
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const signUp = useCallback(async (credentials: {
    email: string;
    password: string;
    role: 'patient' | 'pharmacy';
    firstName?: string;
    lastName?: string;
    pharmacyName?: string;
    pharmacyType?: string;
    licenseNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    isPetOwner?: boolean;
    skipNavigation?: boolean;
  }) => {
    try {
      setIsLoading(true);
      
      const { email, password, role, firstName, lastName, pharmacyName, licenseNumber, address, city, state, zipCode, country, isPetOwner, skipNavigation } = credentials;
      
      // Create metadata based on role
      const metadata: Record<string, string | boolean | undefined> = { role };
      
      if (role === 'patient') {
        metadata.first_name = firstName;
        metadata.last_name = lastName;
        metadata.is_pet_owner = isPetOwner || false;
        // 2026-06-02 Cortana (UAT msg 34795): persist patient state on profile
        // so the state-license gate can match the patient with licensed
        // pharmacies. Goes into the Supabase user metadata which the
        // handle_new_user() DB trigger reads into profiles.state.
        if (state) {
          metadata.state = state;
        }
      }

      if (country) {
        metadata.country = country;
      }

      // Pharmacy details go in with the account itself (2026-10-15). They were
      // saved by a separate updateUser call after sign-up, which needs a
      // session; with email confirmation on there is none yet, so the call
      // failed and the Stripe webhook found no pharmacy details after payment.
      if (role === 'pharmacy') {
        Object.assign(metadata, {
          pending_pharmacy_name: pharmacyName || '',
          pending_pharmacy_type: credentials.pharmacyType || 'standard',
          pending_license: licenseNumber || '',
          pending_address: address || '',
          pending_city: city || '',
          pending_state: state || '',
          pending_zip: zipCode || '',
          pending_country: country || 'United States',
        });
      }

      // Admin order: patients go straight to /upload-prescription after email confirm
      // Pharmacies still land on /dashboard (their command centre)
      const postConfirmPath = role === 'pharmacy' ? '/dashboard' : '/upload-prescription';

      const { data, error } = await (await getSupabase()).auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: window.location.origin + postConfirmPath
        },
      });

      if (error) {
        // 2026-05-28 (PR #178): suppress the generic toast here.
        // RegisterForm now classifies the error and shows a friendly,
        // actionable toast via classifySignupError(). Showing both
        // resulted in stacked toasts with one technical + one friendly
        // message. Caller is responsible for surfacing the error.
        return { error, data: null };
      }

      // PAYMENT GATE (2026-05-30): Do NOT create pharmacy record here.
      // The Stripe webhook (customer.subscription.created) creates it after the
      // subscription starts, from the pending_* details saved with the account.
      // The patient welcome email is queued by a database trigger on sign-up and
      // sent by process-email-sequence. (Round 69: the browser no longer sends a
      // second copy; send-notification now takes admin or service role calls only.)
      
      // Skip navigation if caller handles redirect (e.g., pharmacy Stripe checkout)
      if (!skipNavigation) {
        if (!data.session) {
          toast({
            title: 'Registration successful',
            description: 'Please check your email to verify your account.',
          });
          // location.state not available in Next.js - passing via URL params
          router.push(`/verify-email?email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}`);
        } else {
          toast({
            title: 'Welcome to Script Unlock',
            description: 'Send your prescription to pharmacies for cash price quotes.',
          });
          // Send patients directly to upload - fastest path to value
          router.push(role === 'patient' ? '/upload-prescription' : '/dashboard');
        }
      }

      return { error: null, data };
    } catch (error: any) {
      toast({
        title: 'Registration failed',
        description: error.message,
        variant: 'destructive',
      });
      return { error, data: null };
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const signOut = useCallback(async () => {
    try {
      await (await getSupabase()).auth.signOut();
      setProfile(null);
      router.push('/');
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    } catch (error: any) {
      toast({
        title: 'Error signing out',
        description: error.message,
        variant: 'destructive',
      });
    }
  }, [router]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    try {
      const { data, error } = await (await getSupabase()).auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${patientHomeAfterSignIn()}`,
        },
      });

      if (error) {
        toast({
          title: 'Failed to send magic link',
          description: error.message,
          variant: 'destructive',
        });
        return { error, data: null };
      }

      toast({
        title: 'Check your email',
        description: 'We sent you a magic link to sign in.',
      });
      return { error: null, data };
    } catch (error: any) {
      toast({
        title: 'Failed to send magic link',
        description: error.message,
        variant: 'destructive',
      });
      return { error, data: null };
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  }, [user?.id, fetchProfile]);

  /** Initiate TOTP enrollment - returns QR code + secret for display */
  const enrollMFA = useCallback(async () => {
    try {
      const { data, error } = await (await getSupabase()).auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Script Unlock Authenticator',
      });
      if (error) return { factorId: null, qrCode: null, secret: null, error };
      return {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        error: null,
      };
    } catch (err: any) {
      return { factorId: null, qrCode: null, secret: null, error: err };
    }
  }, []);

  /** Complete a TOTP challenge. Used by the post-login gate and re-verification flows. */
  const verifyMFA = useCallback(async (factorId: string, code: string) => {
    try {
      const { data: challengeData, error: challengeError } =
        await (await getSupabase()).auth.mfa.challenge({ factorId });
      if (challengeError) return { error: challengeError };

      const { error: verifyError } = await (await getSupabase()).auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      return { error: verifyError ?? null };
    } catch (err: any) {
      return { error: err };
    }
  }, []);

  /** Cancel the pending MFA challenge - signs the user out cleanly. */
  const dismissMFA = useCallback(async () => {
    setMfaPending(false);
    setMfaFactorId(null);
    await (await getSupabase()).auth.signOut();
  }, []);

  const handleMFASuccess = useCallback(() => {
    setMfaPending(false);
    setMfaFactorId(null);
    toast({
      title: 'Login successful',
      description: 'Welcome back to Script Unlock!',
    });
    router.push(pendingNavigateTo.current);
  }, [router]);

  // Loading lasts until the role of a signed-in user is known (see roleReadFor).
  const roleLoading = !!user && roleReadFor !== user.id;

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    session,
    user,
    profile,
    isLoading: isLoading || roleLoading,
    mfaPending,
    signIn,
    signInWithMagicLink,
    signUp,
    signOut,
    refreshProfile,
    enrollMFA,
    verifyMFA,
    dismissMFA,
  }), [
    session, user, profile, isLoading, roleLoading, mfaPending,
    signIn, signInWithMagicLink, signUp, signOut, refreshProfile,
    enrollMFA, verifyMFA, dismissMFA,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Post-login MFA gate - rendered when signIn detects enrolled TOTP factors */}
      {mfaPending && <MFAVerifyChallenge
        open={mfaPending}
        factorId={mfaFactorId}
        onSuccess={handleMFASuccess}
        onCancel={dismissMFA}
      />}
    </AuthContext.Provider>
  );
};
